import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';

import type { ArAgingPayload } from './api';
import { ageLabels, money, share } from './format';

export type ArResponsibility = 'all' | 'insurance' | 'patient';

export function ArResponsibilityView({
  data,
  responsibility,
  onResponsibility,
}: {
  data: ArAgingPayload;
  responsibility: ArResponsibility;
  onResponsibility: (value: ArResponsibility) => void;
}) {
  const insurance = data.buckets ?? [];
  const patient = data.patients.buckets ?? [];
  const rows = [
    {
      key: 'insurance' as const,
      label: 'Insurance',
      balance: data.balance_cents,
      buckets: insurance,
    },
    {
      key: 'patient' as const,
      label: 'Patient responsibility',
      balance: data.patients.all_balance_cents,
      buckets: patient,
    },
  ];
  const selected = rows.filter(row => responsibility === 'all' || row.key === responsibility);
  const buckets = ageLabels.map((label, bucket) => ({
    label,
    bucket,
    insurance:
      responsibility === 'patient'
        ? 0
        : (insurance.find(b => b.bucket === bucket)?.balance_cents ?? 0),
    patient:
      responsibility === 'insurance'
        ? 0
        : (patient.find(b => b.bucket === bucket)?.balance_cents ?? 0),
  }));
  const total = selected.reduce((sum, row) => sum + (row.balance ?? 0), 0);
  const old = buckets
    .filter(b => b.bucket === 3 || b.bucket === 4)
    .reduce((sum, b) => sum + b.insurance + b.patient, 0);
  const maximum = Math.max(1, ...buckets.map(b => b.insurance + b.patient));
  return (
    <>
      <p className="rw-footnote">As of {data.as_of}</p>
      <ToggleButtonGroup
        value={responsibility}
        exclusive
        size="small"
        aria-label="A/R responsibility"
        onChange={(_, value: ArResponsibility | null) => {
          if (value) onResponsibility(value);
        }}
        sx={{ mb: 3, flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none' } }}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="insurance">Insurance</ToggleButton>
        <ToggleButton value="patient">Patient</ToggleButton>
      </ToggleButtonGroup>
      <div className="rw-summary rw-responsibility-summary">
        <section className="rw-lead">
          <div className="rw-eyebrow">
            {responsibility === 'all'
              ? 'Total accounts receivable'
              : responsibility === 'insurance'
                ? 'Insurance accounts receivable'
                : 'Patient accounts receivable'}
          </div>
          <div className="rw-big">{money(total)}</div>
          <p>Positive net balances by account and responsibility</p>
        </section>
        <section>
          <div className="rw-eyebrow">Insurance outstanding</div>
          <div className="rw-stat">{money(data.balance_cents)}</div>
          <p>Posted payer responsibility</p>
        </section>
        <section>
          <div className="rw-eyebrow">Patient outstanding</div>
          <div className="rw-stat">{money(data.patients.all_balance_cents)}</div>
          <p>After patient payments and credits</p>
        </section>
        <section>
          <div className="rw-eyebrow">Older than 90 days</div>
          <div className="rw-stat">{money(old)}</div>
          <p>{share(old, total)} of selected responsibility</p>
        </section>
      </div>
      <section className="rw-panel">
        <div className="rw-panel-head">
          <div>
            <h3>Where balances are aging</h3>
            <p>Insurance and patient responsibility share the same age bands.</p>
          </div>
        </div>
        <div className="rw-responsibility-chart">
          {buckets.map(b => (
            <div
              key={b.bucket}
              className="rw-responsibility-bar"
              aria-label={`${b.label} days: Insurance ${money(b.insurance)}, Patient ${money(b.patient)}`}
            >
              <strong>{money(b.insurance + b.patient)}</strong>
              <div className="rw-responsibility-track">
                <Box sx={{ height: `${(100 * b.patient) / maximum}%`, background: '#c18451' }} />
                <Box sx={{ height: `${(100 * b.insurance) / maximum}%`, background: '#6550af' }} />
              </div>
              <span>{b.label}</span>
            </div>
          ))}
        </div>
        <div className="rw-responsibility-legend">
          <span>
            <Box component="i" sx={{ background: '#6550af' }} /> Insurance
          </span>
          <span>
            <Box component="i" sx={{ background: '#c18451' }} /> Patient responsibility
          </span>
        </div>
        {total === 0 && (
          <p className="rw-context-note">No positive posted balances for this responsibility.</p>
        )}
      </section>
      <section className="rw-panel">
        <div className="rw-panel-head">
          <div>
            <h3>Who owes the balance?</h3>
            <p>Patient responsibility is separate from insurance, not an additional age band.</p>
          </div>
        </div>
        <div className="rw-table-wrap">
          <table aria-label="A/R by responsibility">
            <thead>
              <tr>
                <th>Responsibility</th>
                {buckets.map(b => (
                  <th key={b.bucket}>{b.label}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {selected.map(row => (
                <tr key={row.key}>
                  <th>{row.label}</th>
                  {buckets.map(b => (
                    <td key={b.bucket}>
                      {money(row.buckets.find(cell => cell.bucket === b.bucket)?.balance_cents)}
                    </td>
                  ))}
                  <td>
                    <strong>{money(row.balance)}</strong>
                  </td>
                </tr>
              ))}
              <tr>
                <th>Total</th>
                {buckets.map(b => (
                  <td key={b.bucket}>{money(b.insurance + b.patient)}</td>
                ))}
                <td>
                  <strong>{money(total)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      <div className="rw-context-note">
        {data.definition} {data.patient_scope}
      </div>
    </>
  );
}
